import type { SearchCollectionKey } from '@shared/constants/searchCollections';
import { type AppLinkOptions, appLinks } from 'src/lib/routing';

/**
 * Per-collection UI contract for the palette — the ONLY place that knows
 * collection-specific display + navigation; a new collection is one entry.
 *
 *   this module  ──▶ WHAT a doc is: identifier · typeLabel · slots · destination
 *   ACCENT table ──▶ how it LOOKS: icon + colour (Tailwind stays out)
 *
 * The result-card anatomy each entry drives:
 *
 *   [icon]  SO-00612  SALES ORDER                       [Picking]
 *           CUST Sysco West   REF PO-4471
 *   ▲identifier (mono) · typeLabel (uppercase) · status badge right
 *           ▲ up to 3 labelled slots; the slot the query MATCHED leads, marked
 *
 * `slots` is a MAP keyed by field, deliberately modelled on this repo's MUI
 * grid column defs (`DataGrid.tsx` `GridColObj = Record<key, GridColValue>`):
 *   the map KEY is the field, insertion order is priority, `label` ≈ headerName,
 *   `get` ≈ valueGetter, `render` ≈ renderCell (absent ⇒ default rendering).
 *
 *   deep-links ✓  orders (slug + orderType) · bills · payments · runs ·
 *                 transfers (slug) · accounts (accountPath) ·
 *                 lots (warehouseId + slug, list page when warehouseId is null)
 *   list page  ✗  pick/receiving tickets — the projection drops
 *                 the composite route params their pages require
 *
 * Business Entities is deliberately absent: the collection exists, but
 * `mintScopedSearchKey` cannot grant it into a user key yet, so it would never
 * reach `collections`. The ACCENT table already carries its accent, so
 * enabling the group later is one entry here + the mint-filter change.
 */

/** A projected Typesense document. Every field arrives as a string or number. */
export type SearchDocument = Record<string, unknown>;

/**
 * Which accent a row wears; the ACCENT table maps it to icon + colour.
 * `orders`/`bills` vary it per source — SO vs PO, invoice vs payable.
 */
export type AccentKey =
  | 'sales-order'
  | 'purchase-order'
  | 'buysell-order'
  | 'work-order'
  | 'product'
  | 'lot'
  | 'production-run'
  | 'invoice'
  | 'payable'
  | 'payment'
  | 'inventory-transfer'
  | 'receiving-ticket'
  | 'pick-ticket'
  | 'ledger-account'
  | 'business-entity';

/** Read a non-empty string field, or `undefined`. */
export function readString(doc: SearchDocument, field: string): string | undefined {
  const value = doc[field];
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

/** Read a finite numeric field, or `undefined` — an optional int the projection may omit. */
export function readNumber(doc: SearchDocument, field: string): number | undefined {
  const value = doc[field];
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

/**
 * One labelled line-2 detail slot. The owning MAP key is the Typesense field
 * (and default highlight key); insertion order is priority.
 */
export interface SlotDef<D extends SearchDocument = SearchDocument> {
  /** Small uppercase label ("CUST", "REF"); a function for entity-dependent labels. */
  label: string | ((doc: D) => string);
  /** Slot value; default reads the map key off the doc as a non-empty string. */
  get?: (doc: D) => string | undefined;
  /** Highlight key to promote/mark this slot; default is the map key. */
  highlightKey?: string;
  /** Text-blob slot that must NEVER persist into localStorage recents. */
  transient?: boolean;
}

export type SlotMap<D extends SearchDocument = SearchDocument> = Record<string, SlotDef<D>>;

export interface CollectionUiConfig<D extends SearchDocument = SearchDocument> {
  key: SearchCollectionKey;
  /** Singular result-type label (facet chip + group header, pluralised by the UI). */
  label: string;
  /** Comma-joined Typesense `query_by` fields (must exist in the collection schema). */
  queryBy: string;
  /** Comma-joined `query_by_weights`, positionally aligned to `queryBy`. */
  queryByWeights?: string;
  /** Comma-joined `num_typos`, positionally aligned to `queryBy`. */
  numTypos?: string;
  /** True when the collection has a faceted `status` field (drives scoped sub-facets). */
  hasStatus: boolean;
  /** Accent identity for the row (per-document so orders/bills vary by source). */
  accentKey: (doc: D) => AccentKey;
  /** Line-1 monospace identifier (SO-00529, a lot number, an account number…). */
  identifier: (doc: D) => string | undefined;
  /** Line-1 uppercase entity-type label (SALES ORDER, LOT, INVOICE…), per-document. */
  typeLabel: (doc: D) => string;
  /** Status enum for the row's status pill, if the entity has one. */
  status?: (doc: D) => string | undefined;
  /** Native destination for the document. */
  destination: (doc: D) => AppLinkOptions;
  /** Ordered line-2 detail slots (insertion order = priority; ≤3 render). */
  slots: SlotMap<D>;
}

/** Read the `label` of a slot, resolving the entity-dependent function form. */
export function slotLabel(def: SlotDef, doc: SearchDocument): string {
  return typeof def.label === 'function' ? def.label(doc) : def.label;
}

/** The slot value: the def's `get`, else the map key read as a non-empty string. */
export function slotValue(field: string, def: SlotDef, doc: SearchDocument): string | undefined {
  return def.get ? def.get(doc) : readString(doc, field);
}

/** The highlight key a slot promotes on: the def's override, else the map key. */
export function slotHighlightKey(field: string, def: SlotDef): string {
  return def.highlightKey ?? field;
}

/** The optional `query_by_weights` / `num_typos` a config contributes to its leg. */
export function buildRankingParams(config: CollectionUiConfig): { query_by_weights?: string; num_typos?: string } {
  return {
    ...(config.queryByWeights !== undefined ? { query_by_weights: config.queryByWeights } : {}),
    ...(config.numTypos !== undefined ? { num_typos: config.numTypos } : {}),
  };
}

// Orders fold four source tables into one collection; `orderType` selects page,
// label, accent, and the counterparty label.
const ORDER_DESTINATION_BY_TYPE: Record<string, (slug: string) => AppLinkOptions> = {
  SALES_ORDER: (slug) => appLinks.salesOrder({ slug }),
  PURCHASE_ORDER: (slug) => appLinks.purchaseOrder({ slug }),
  BUYSELL_ORDER: (slug) => appLinks.buySellOrder({ slug }),
  WORK_ORDER: (slug) => appLinks.growerProduct({ slug }),
};

const ORDER_TYPE_LABEL: Record<string, string> = {
  SALES_ORDER: 'SALES ORDER',
  PURCHASE_ORDER: 'PURCHASE ORDER',
  BUYSELL_ORDER: 'BUY-SELL',
  WORK_ORDER: 'GROWER PRODUCT',
};

const ORDER_ACCENT_BY_TYPE: Record<string, AccentKey> = {
  SALES_ORDER: 'sales-order',
  PURCHASE_ORDER: 'purchase-order',
  BUYSELL_ORDER: 'buysell-order',
  WORK_ORDER: 'work-order',
};

const ORDER_COUNTERPARTY_LABEL: Record<string, string> = {
  SALES_ORDER: 'CUST',
  PURCHASE_ORDER: 'VENDOR',
  WORK_ORDER: 'GROWER',
  BUYSELL_ORDER: 'PARTIES',
};

const orderCounterpartyLabel = (doc: SearchDocument): string =>
  ORDER_COUNTERPARTY_LABEL[readString(doc, 'orderType') ?? ''] ?? 'PARTY';

const CONFIGS: CollectionUiConfig[] = [
  {
    key: 'orders',
    label: 'Order',
    // queryBy / queryByWeights / numTypos are positionally aligned — keep lengths equal.
    queryBy: 'slugTokens,slug,counterpartyName,reference,lineItemsText,notesText',
    queryByWeights: '127,110,4,4,1,1',
    numTypos: '0,0,2,2,2,2',
    hasStatus: true,
    accentKey: (doc) => ORDER_ACCENT_BY_TYPE[readString(doc, 'orderType') ?? ''] ?? 'sales-order',
    identifier: (doc) => readString(doc, 'slug'),
    typeLabel: (doc) => ORDER_TYPE_LABEL[readString(doc, 'orderType') ?? ''] ?? 'ORDER',
    status: (doc) => readString(doc, 'status'),
    destination: (doc) => {
      const orderType = readString(doc, 'orderType') ?? '';
      const slug = readString(doc, 'slug');
      const builder = ORDER_DESTINATION_BY_TYPE[orderType];
      return slug && builder ? builder(slug) : appLinks.landing();
    },
    slots: {
      counterpartyName: { label: orderCounterpartyLabel },
      reference: { label: 'REF' },
      notesText: { label: 'NOTES', transient: true },
    },
  },
  {
    key: 'products',
    label: 'Product',
    queryBy: 'description,productCode,commodity,commodityLabel',
    hasStatus: false,
    accentKey: () => 'product',
    identifier: (doc) => readString(doc, 'productCode'),
    typeLabel: () => 'PRODUCT',
    // The product doc carries no per-product route slug; land on Inventory.
    destination: () => appLinks.inventory(),
    slots: {
      description: { label: 'DESC' },
      commodity: {
        label: 'COMMODITY',
        get: (doc) => readString(doc, 'commodityLabel') ?? readString(doc, 'commodity'),
      },
      countryOfOrigin: { label: 'ORIGIN' },
    },
  },
  {
    key: 'lots',
    label: 'Lot',
    queryBy: 'lotNumber,commodity,lotProductsText,notesText',
    hasStatus: true,
    accentKey: () => 'lot',
    identifier: (doc) => readString(doc, 'lotNumber'),
    typeLabel: () => 'LOT',
    status: (doc) => readString(doc, 'status'),
    destination: (doc) => {
      const warehouseId = readNumber(doc, 'warehouseId');
      const slug = readString(doc, 'lotNumber');

      return warehouseId !== undefined && slug ? appLinks.viewLotTracking({ warehouseId, slug }) : appLinks.inventory();
    },
    slots: { commodity: { label: 'COMMODITY' }, notesText: { label: 'NOTES', transient: true } },
  },
  {
    key: 'productionRuns',
    label: 'Production Run',
    queryBy: 'reference,componentsText,notesText',
    hasStatus: true,
    accentKey: () => 'production-run',
    identifier: (doc) => readString(doc, 'reference'),
    typeLabel: () => 'PRODUCTION RUN',
    status: (doc) => readString(doc, 'status'),
    destination: (doc) => {
      const slug = readString(doc, 'reference');
      return slug ? appLinks.productionRun({ slug }) : appLinks.productionRuns();
    },
    slots: { notesText: { label: 'NOTES', transient: true } },
  },
  {
    key: 'bills',
    label: 'Bill',
    queryBy: 'billNumber,reference,counterpartyName,lineItemsText,notesText',
    hasStatus: true,
    accentKey: (doc) => (readString(doc, 'billType') === 'PAYABLE' ? 'payable' : 'invoice'),
    identifier: (doc) => readString(doc, 'billNumber'),
    typeLabel: (doc) => (readString(doc, 'billType') === 'PAYABLE' ? 'PAYABLE' : 'INVOICE'),
    status: (doc) => readString(doc, 'status'),
    destination: (doc) => {
      const slug = readString(doc, 'billNumber');
      return slug ? appLinks.bill({ slug }) : appLinks.invoices();
    },
    slots: {
      // `counterpartyName` is the bill's businessEntity — a customer on an
      // INVOICE, a vendor on a PAYABLE — so the label follows `billType`.
      counterpartyName: { label: (doc) => (readString(doc, 'billType') === 'PAYABLE' ? 'VENDOR' : 'CUST') },
      reference: { label: 'REF' },
      notesText: { label: 'NOTES', transient: true },
    },
  },
  {
    key: 'payments',
    label: 'Payment',
    queryBy: 'reference,counterpartyName,paymentDetails',
    hasStatus: true,
    accentKey: () => 'payment',
    identifier: (doc) => readString(doc, 'reference'),
    typeLabel: () => 'PAYMENT',
    // The projection folds `reconciliationStatus` INTO the doc's `status` field
    // (payments have no lifecycle status column), so it may legitimately be absent.
    status: (doc) => readString(doc, 'status'),
    destination: (doc) => {
      const slug = readString(doc, 'reference');
      return slug ? appLinks.payment({ slug }) : appLinks.payments();
    },
    slots: { counterpartyName: { label: 'PARTY' }, paymentDetails: { label: 'DETAILS', transient: true } },
  },
  {
    key: 'inventoryTransfers',
    label: 'Inventory Transfer',
    queryBy: 'reference,itemsText,notesText',
    hasStatus: true,
    accentKey: () => 'inventory-transfer',
    identifier: (doc) => readString(doc, 'reference'),
    typeLabel: () => 'TRANSFER',
    status: (doc) => readString(doc, 'status'),
    destination: (doc) => {
      const slug = readString(doc, 'reference');
      return slug ? appLinks.inventoryTransfer({ slug }) : appLinks.inventoryTransfers();
    },
    slots: { notesText: { label: 'NOTES', transient: true } },
  },
  {
    key: 'receivingTickets',
    label: 'Receiving Ticket',
    queryBy: 'slug,reference,counterpartyName,lineItemsText,notesText',
    hasStatus: true,
    accentKey: () => 'receiving-ticket',
    identifier: (doc) => readString(doc, 'slug'),
    typeLabel: () => 'RECEIVING TICKET',
    status: (doc) => readString(doc, 'status'),
    destination: (doc) => {
      const slug = readString(doc, 'slug');
      return slug ? appLinks.editReceivingTicket({ slug }) : appLinks.viewReceivingTickets();
    },
    slots: {
      counterpartyName: { label: (doc) => (readString(doc, 'ticketType') === 'GP' ? 'GROWER' : 'VENDOR') },
      reference: { label: 'REF' },
      notesText: { label: 'NOTES', transient: true },
    },
  },
  {
    key: 'pickTickets',
    label: 'Pick Ticket',
    queryBy: 'slug,reference,counterpartyName,lineItemsText',
    hasStatus: true,
    accentKey: () => 'pick-ticket',
    identifier: (doc) => readString(doc, 'slug'),
    typeLabel: () => 'PICK TICKET',
    status: (doc) => readString(doc, 'status'),
    destination: (doc) => {
      const slug = readString(doc, 'slug');
      return slug ? appLinks.editPickTicket({ slug }) : appLinks.viewPickTickets();
    },
    slots: { counterpartyName: { label: 'CUST' }, reference: { label: 'REF' } },
  },
  {
    key: 'ledgerAccounts',
    label: 'Account',
    queryBy: 'name,accountNumber,accountPath,memosText',
    hasStatus: false,
    accentKey: () => 'ledger-account',
    identifier: (doc) => readString(doc, 'accountNumber') ?? readString(doc, 'name'),
    typeLabel: () => 'ACCOUNT',
    // No status badge: ledger accounts have no lifecycle status. `accountType`
    // (PascalCase classifications like `CurrentAssets`) is not in StatusBadge's
    // status palette, so rendering it yields a raw uncoloured pill — omit it.
    // Ledger accounts deep-link to the Account Register, not a detail page.
    destination: (doc) => {
      const path = readString(doc, 'accountPath');
      return path ? appLinks.accountRegister({ path }) : appLinks.accountRegisterNoPath();
    },
    slots: { name: { label: 'NAME' }, accountPath: { label: 'PATH' }, memosText: { label: 'MEMOS', transient: true } },
  },
];

const CONFIG_BY_KEY = new Map<string, CollectionUiConfig>(CONFIGS.map((config) => [config.key, config]));

/** Look up the UI config for a collection alias (alias === key across the registry). */
export function getCollectionConfig(key: string): CollectionUiConfig | undefined {
  return CONFIG_BY_KEY.get(key);
}

/**
 * Slot fields a collection must never round-trip into localStorage recents
 * (text blobs — `lineItemsText`, `notesText`, …). Derived from the per-slot
 * `transient` flag so the fact lives next to the field it describes.
 */
export function transientSlotFields(key: string): ReadonlySet<string> {
  const config = getCollectionConfig(key);
  if (!config) return new Set();
  return new Set(Object.entries(config.slots).flatMap(([field, def]) => (def.transient ? [field] : [])));
}
